import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProductImage } from "@/components/ProductImage";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { formatCurrency, getLocalizedField, isOrderable, packSize, productImages, productText, unitLabel } from "@/lib/product";
import { veldWaarde } from "@/lib/pdpTemplate";
import { pastBij } from "@/lib/pastBij";

const isNextBrush = (product) => /nextbrush/i.test(product?.merk || "") || /^NB/i.test(product?.sku || "");

/** Tabs: Omschrijving | Ingrediënten & gebruik (Frankly) / Materiaal & gebruik (NextBrush) | Productgegevens. Lege tabs verdwijnen. */
export const PdpTabs = ({ blok, product, ctx }) => {
  const inci = veldWaarde("inci", product, ctx);
  const gebruik = productText(product, "gebruik", ctx.lang) || product?.gebruik || "";
  const materiaal = productText(product, "materiaal", ctx.lang) || product?.materiaal || "";
  const rijen = (blok.velden || [])
    .map((key) => ({ key, label: ctx.t(`pdp.field.${key}`), value: veldWaarde(key, product, ctx) }))
    .filter((r) => r.value != null && r.value !== "");
  const nb = isNextBrush(product);

  const tabs = [
    ctx.omschrijving && {
      id: "omschrijving",
      label: ctx.t("product.description"),
      body: <p className="max-w-prose whitespace-pre-line text-[15px] leading-7 text-foreground/70" data-testid="product-description">{ctx.omschrijving}</p>,
    },
    (inci || gebruik || materiaal) && {
      id: "ingredienten",
      label: nb ? ctx.t("pdp.tabMaterial") : ctx.t("pdp.tabIngredients"),
      body: (
        <div className="space-y-4">
          {materiaal && <p className="max-w-prose whitespace-pre-line text-[15px] leading-7 text-foreground/70" data-testid="pdp-tab-materiaal">{materiaal}</p>}
          {inci && (
            <div data-testid="pdp-block-inci">
              <h3 className="text-xs uppercase tracking-widest text-muted-foreground">{ctx.t("pdp.inciTitle")}</h3>
              <p className="mt-2 max-w-prose break-words text-[13px] leading-6 text-foreground/60" data-testid="pdp-block-inci-body">{inci}</p>
            </div>
          )}
          {gebruik && (
            <div data-testid="pdp-tab-gebruik">
              <h3 className="text-xs uppercase tracking-widest text-muted-foreground">{ctx.t("pdp.usageTitle")}</h3>
              <p className="mt-2 max-w-prose whitespace-pre-line text-[15px] leading-7 text-foreground/70">{gebruik}</p>
            </div>
          )}
        </div>
      ),
    },
    rijen.length > 0 && {
      id: "gegevens",
      label: ctx.t("pdp.tabSpecs"),
      body: (
        <dl className="divide-y divide-border/50 text-sm leading-6" data-testid="pdp-block-velden">
          {rijen.map((r) => (
            <div key={r.key} className="flex justify-between gap-4 py-2" data-testid={`pdp-field-${r.key}`}>
              <dt className="text-muted-foreground">{r.label}</dt>
              <dd className="max-w-[60%] break-words text-right font-medium tabular-nums">{r.value}</dd>
            </div>
          ))}
        </dl>
      ),
    },
  ].filter(Boolean);

  if (tabs.length === 0) return null;
  return (
    <Tabs defaultValue={tabs[0].id} data-testid="pdp-block-tabs">
      <TabsList className="h-auto w-full justify-start gap-1 overflow-x-auto rounded-none border-b bg-transparent p-0">
        {tabs.map((tab) => (
          <TabsTrigger
            key={tab.id}
            value={tab.id}
            className="rounded-none border-b-2 border-transparent px-3 py-2.5 text-sm data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
            data-testid={`pdp-tab-${tab.id}`}
          >
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {tabs.map((tab) => (
        <TabsContent key={tab.id} value={tab.id} className="pt-4" data-testid={`pdp-tab-content-${tab.id}`}>
          {tab.body}
        </TabsContent>
      ))}
    </Tabs>
  );
};

/** "Maak de routine compleet": gerelateerde producten, met één klik (per besteleenheid) in het mandje. */
export const PdpPastBij = ({ product, ctx }) => {
  const { producten, account } = useAuth();
  const { addLine } = useCart();
  const [alle, setAlle] = useState(null);

  useEffect(() => {
    if (!account) return;
    let actief = true;
    producten().then((d) => actief && setAlle(Array.isArray(d) ? d : [])).catch(() => actief && setAlle([]));
    return () => { actief = false; };
  }, [account, producten]);

  if (!account || !alle) return null;
  const items = pastBij(product, alle);
  if (items.length === 0) return null;

  const voeg = (p) => {
    const qty = packSize(p) || 1;
    addLine(p, qty);
    toast.success(ctx.t("product.added", { naam: getLocalizedField(p, "naam", ctx.lang) }));
  };

  return (
    <section className="mt-10" data-testid="pdp-block-past-bij">
      <h2 className="font-display text-xl font-semibold tracking-tight">{ctx.t(isNextBrush(product) ? "pdp.pastBijTitleNb" : "pdp.pastBijTitle")}</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((p) => {
          const naam = getLocalizedField(p, "naam", ctx.lang);
          const pack = packSize(p) || 1;
          const kan = isOrderable(p, account);
          return (
            <Card key={p.id} className="flex flex-col overflow-hidden" data-testid={`past-bij-${p.sku}`}>
              <Link to={`/producten/${p.id}`} className="block bg-[hsl(var(--photo-bg))]">
                <ProductImage src={productImages(p)[0]} alt={naam} className="aspect-square w-full" imgClassName="p-5" width={480} />
              </Link>
              <div className="flex flex-1 flex-col gap-2 p-4">
                <Link to={`/producten/${p.id}`} className="text-sm font-medium leading-snug hover:underline">{naam}</Link>
                <p className="text-xs text-muted-foreground tabular-nums">
                  {formatCurrency(p.verkoopprijs, ctx.lang)} {ctx.t("price.exclVat")}
                </p>
                <Button size="sm" variant="outline" className="mt-auto" disabled={!kan} onClick={() => voeg(p)} data-testid={`past-bij-add-${p.sku}`}>
                  <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                  {pack} {unitLabel(p.eenheid || ctx.t("cart.pieces"), pack, ctx.lang)}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </section>
  );
};
